import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { submitEmailSignup } from "@/lib/api";
import { IMG } from "@/lib/images";
import { VipMemberCard } from "@/components/VipMemberCard";
import { setMyMemberNumber } from "@/lib/referral";

export const EmailSignup = ({ source = "homepage", compact = false }) => {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [member, setMember] = useState(null);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSubmitting(true);
    try {
      const data = await submitEmailSignup(email, source);
      setMyMemberNumber(data.member_number);
      setMember({
        memberNumber: data.member_number,
        email: data.email,
        resubscribed: !!data.resubscribed,
      });
      toast.success(
        data.resubscribed
          ? "Welcome back to the VIP Crew."
          : `You're in! Your VIP number is #${String(data.member_number).padStart(3, "0")} — ride safe, we'll be in touch soon.`
      );
      setEmail("");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail || "Signup failed. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (compact) {
    if (member) {
      return (
        <div data-testid="email-signup-compact-success" className="w-full">
          <VipMemberCard
            memberNumber={member.memberNumber}
            email={member.email}
            resubscribed={member.resubscribed}
          />
        </div>
      );
    }
    return (
      <form onSubmit={onSubmit} className="flex flex-col sm:flex-row gap-3 w-full">
        <input
          data-testid="email-signup-input"
          type="email"
          required
          placeholder="your@email.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="flex-1 bg-[#1A1A1D] border border-white/10 rounded-sm px-4 py-3 text-white placeholder:text-white/30 focus:border-[#F47A20] focus:ring-1 focus:ring-[#F47A20] outline-none"
        />
        <button data-testid="email-signup-submit" type="submit" disabled={submitting} className="btn-primary justify-center">
          {submitting ? "..." : "Join VIP"}
          <ArrowRight size={16} />
        </button>
      </form>
    );
  }

  return (
    <section id="vip" data-testid="email-signup-section" className="relative py-28 md:py-40 bg-[#0A0A0C] overflow-hidden">
      <div className="absolute inset-0 opacity-35">
        <img
          src={IMG.RIDE_COAST}
          alt=""
          style={{ objectPosition: "50% 50%" }}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-[#0A0A0C]/85" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0C] via-[#0A0A0C]/70 to-transparent" />
      </div>

      <div className="relative z-10 max-w-3xl mx-auto px-6 md:px-12 text-center">
        {member ? (
          <motion.div
            data-testid="email-signup-success"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex items-center justify-center gap-3 mb-6">
              <span className="section-mark" />
              <span className="overline">You&rsquo;re In &middot; VIP Priority Access</span>
              <span className="section-mark" />
            </div>
            <h2 className="h-display text-white text-4xl sm:text-5xl mb-10">
              You are a <span className="text-[#F47A20]">founding member</span>.
            </h2>
            <VipMemberCard
              memberNumber={member.memberNumber}
              email={member.email}
              resubscribed={member.resubscribed}
            />
            <p className="serial text-[10px] text-white/45 mt-8">
              SAVE THIS PAGE OR CHECK YOUR INBOX FOR YOUR WELCOME EMAIL
            </p>
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            {/* Prototype status — replaces countdown */}
            <div data-testid="prototype-status" className="inline-flex flex-col items-center gap-3 mb-10">
              <span className="tactical-badge">
                <span className="w-1.5 h-1.5 rounded-full bg-[#F47A20] animate-pulse" />
                Prototype V1 Coming Soon
              </span>
              <span className="serial text-[11px] text-white/60">
                JOIN THE VIP LIST FOR FIRST ACCESS
              </span>
            </div>

            <div className="flex items-center justify-center gap-3 mb-6">
              <span className="section-mark" />
              <span className="overline">VIP Waitlist &middot; The OUTLAW V1</span>
              <span className="section-mark" />
            </div>

            <h2 className="h-display text-white text-4xl sm:text-6xl lg:text-7xl">
              Be first to see <br />
              <span className="text-[#F47A20]">The OUTLAW V1.</span>
            </h2>

            <p className="mt-8 text-white/70 max-w-xl mx-auto text-base sm:text-lg">
              Founding members get a unique VIP number, priority access to the OUTLAW V1 launch, and pre-orders before the public &mdash; plus every Founder Journal &amp; behind-the-build update from John.
            </p>

            <form onSubmit={onSubmit} className="mt-10 flex flex-col sm:flex-row gap-3 max-w-xl mx-auto">
              <input
                data-testid="email-main-input"
                type="email"
                required
                placeholder="rider@roadrig.com.au"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="flex-1 bg-[#1A1A1D]/80 backdrop-blur border border-white/15 rounded-sm px-5 py-4 text-white placeholder:text-white/30 focus:border-[#F47A20] focus:ring-1 focus:ring-[#F47A20] outline-none"
              />
              <button data-testid="email-main-submit" type="submit" disabled={submitting} className="btn-primary justify-center">
                {submitting ? "Joining..." : "Claim VIP Number"}
                <ArrowRight size={18} />
              </button>
            </form>

            <p className="serial text-[10px] text-white/40 mt-6">
              UNIQUE VIP NUMBER &middot; PRIORITY LAUNCH ACCESS &middot; EARLY PRE-ORDERS &middot; FOUNDER JOURNAL
            </p>
          </motion.div>
        )}
      </div>
    </section>
  );
};
